import React, { FC, useEffect, useRef, useCallback, useMemo } from "react"
import { useDispatch } from "react-redux"
import type {
    AutoSegmentationRequest,
    ObjectTrackingRequest,
    MLActionProcessorForTask
} from "resha"
import {
    startMonitorApp,
    stopMonitorApp
} from "../../../../../../Actions/API/AppInstance/AppInstanceActions"
import {
    getApiKey,
    selectUserId
} from "../../../../../../Helpers/Functions/Selectors/auth"
import { useAppInstance } from "../../../../../../Hooks/useAppInstance"
import { useAssumedRoles } from "../../../../../../Hooks/useAssumedRoles"
import { useAppState } from "../../../../../../Hooks/useReduxState"
import {
    getValue,
    hasResult,
    hasValue
} from "../../../../../../Models/Client/FetchState"
import { LabelingLayout } from "../../../../../Common/LabelingLayout/LabelingLayout"
import { Loader } from "dash-styled"
import { routePaths } from "../../../../../../Routes"
import { push } from "connected-react-router"
import { uploadData } from "../../../../../../Actions/API/Data/DataActions"
import { randID } from "../../../../../../Helpers/Functions/random"
import { MainThunkDispatch } from "../../../../../../Reducers"
import { useAppTemplate } from "../../../../../../Hooks/useAppTemplate"
import { FilterAndSortExpression } from "../../../../../../Models/Client/FilterAndSortExpression"
import { APIJobQueryModel } from "../../../../../../Models/API/JobModel"
import {
    processAutoSegmentation,
    createObjectTrackingRequest,
    getObjectTrackingResult
} from "../../../../../../Actions/API/MLActions"
import { useFeedbackTags } from "../../../../../../Hooks/useFeedbackTags"
import { useErrorTrackingInfo } from "../../../../../../Hooks/useErrorTrackingInfo"
import { useAIModelAssignmentStatus } from "../../../../../../Hooks/useAIModelAssignmentStatus"
import { setExplanationModal } from "../../../../../../Actions/Client/RouteInfoActions"
import { ExplanationModalType } from "../../../../../../Reducers/Client/RouteInfoReducer"

import { OnboardingType } from "../../../../../../Actions/Types"
import { AppDetailLink } from "../../../../../Common/AppDetailLink/AppDetailLink"
import { failMessage } from "../../../../../../Actions/Client/MessagesActions"
import { metaAI } from "neql"
import { StandaloneTaskApp } from "../../../../../Common/Lazy/StandaloneTaskApp"

interface TaskLabelingWrapperProps {
    orgUsername: string
    appID: string
    jobID?: string
    filter?: string
    selectedJobs?: number[]
    filterAndSort?: FilterAndSortExpression<APIJobQueryModel>
    routedFromMessages?: boolean
    isOnboarding?: boolean
    backPath?: string
}

export const TaskLabelingWrapper: FC<TaskLabelingWrapperProps> = ({
    orgUsername,
    appID,
    jobID,
    filter,
    selectedJobs,
    filterAndSort,
    routedFromMessages,
    isOnboarding,
    backPath
}) => {
    useErrorTrackingInfo("superai.appID", appID)

    const templateFetch = useAppTemplate(appID)

    useErrorTrackingInfo("superai.templateName", getValue(templateFetch)?.name)

    const dispatch = useDispatch<MainThunkDispatch>()
    const instanceFetch = useAppInstance(appID)
    const rolesFetch = useAssumedRoles(appID)
    const apiKey = useAppState(getApiKey)
    const userId = useAppState(selectUserId)
    const toolbarRef = useRef<HTMLDivElement>(null)
    const applicationTags = useFeedbackTags(appID)
    const prelabelStatusFetch = useAIModelAssignmentStatus(
        appID,
        metaAI.Meta_Ai_Assignment_Enum.Prelabel
    )

    const applicationFeedbackOptions = useMemo(() => {
        if (!hasValue(applicationTags)) {
            return undefined
        } else {
            return {
                appUuid: appID,
                feedbackOptionsTemplate: getValue(applicationTags)
            }
        }
    }, [appID, applicationTags])

    const reRouteToAppDetails = useCallback(() => {
        dispatch(
            push(
                !jobID
                    ? routedFromMessages
                        ? routePaths.appDetailMessages(
                              orgUsername,
                              appID,
                              filterAndSort,
                              selectedJobs
                          )
                        : routePaths.appDetail(
                              orgUsername,
                              appID,
                              filterAndSort,
                              selectedJobs
                          )
                    : routedFromMessages
                    ? routePaths.jobsMessageDetail(
                          orgUsername,
                          appID,
                          jobID,
                          filterAndSort,
                          selectedJobs
                      )
                    : routePaths.jobsDetail(
                          orgUsername,
                          appID,
                          jobID,
                          filterAndSort,
                          selectedJobs
                      )
            )
        )
    }, [
        appID,
        dispatch,
        filterAndSort,
        jobID,
        orgUsername,
        routedFromMessages,
        selectedJobs
    ])

    const handleBack = useCallback(
        (modalType?: ExplanationModalType) => {
            if (!hasValue(rolesFetch)) {
                return
            }

            if (modalType) {
                dispatch(setExplanationModal(true, modalType))
            }

            if (!!backPath) {
                dispatch(push(backPath))
            } else {
                reRouteToAppDetails()
            }
        },
        [rolesFetch, backPath, dispatch, reRouteToAppDetails]
    )

    const onTaskSubmit = useMemo(() => {
        if (!isOnboarding) {
            return undefined
        }
        return (resolved: boolean) => {
            dispatch({
                type: OnboardingType.clear
            })

            dispatch(
                push(routePaths.appWelcome(orgUsername, appID), {
                    onboardingCompleted: resolved
                })
            )
        }
    }, [appID, dispatch, isOnboarding, orgUsername])

    const handleUploadBlob = useCallback(
        (blob: Blob, jobID: string) =>
            dispatch(uploadData(blob, `jobs/${jobID}/assets/${randID()}`)),
        [dispatch]
    )

    const handleError = useCallback(
        (message: string, error?: unknown) =>
            dispatch(failMessage(message, error)),
        [dispatch]
    )

    const mlActionProcessor = useMemo<MLActionProcessorForTask>(
        () => ({
            autoSegmentation: (req: AutoSegmentationRequest, taskID: string) =>
                dispatch(
                    processAutoSegmentation(req, { type: "perTask", taskID })
                ),
            createObjectTrackingRequest: (
                req: ObjectTrackingRequest,
                taskID: string
            ) =>
                dispatch(
                    createObjectTrackingRequest(req, {
                        type: "perTask",
                        taskID
                    })
                ),
            getObjectTrackingResult: (trackID: string, taskID: string) =>
                dispatch(
                    getObjectTrackingResult(trackID, {
                        type: "perTask",
                        taskID
                    })
                )
        }),
        [dispatch]
    )

    useEffect(() => {
        dispatch(startMonitorApp(appID))

        return () => {
            dispatch(stopMonitorApp())
        }
    }, [appID, dispatch])

    useEffect(() => {
        // hide intercom chat launcher to not cover submit button
        window.Intercom?.("update", { hide_default_launcher: true })

        return () => {
            window.Intercom?.("update", { hide_default_launcher: false })
        }
    }, [])

    const t = useMemo(() => {
        const template = getValue(templateFetch)
        return template
            ? { protocol: template.protocol, visibility: template.visibility }
            : undefined
    }, [templateFetch])

    // TODO disabled for till its fixed to show proper count
    // const [incompleteJobs, setIncompleteJobs] = useState(-1)

    if (
        !hasValue(rolesFetch) ||
        (!hasResult(prelabelStatusFetch) && !isOnboarding)
    ) {
        return <Loader absolute={true} mask={false} />
    }

    return (
        <LabelingLayout
            orgUsername={orgUsername}
            mode="task"
            instanceFetch={instanceFetch}
            onBack={handleBack}
            toolbar={<div ref={toolbarRef} />}
            // TODO disabled for till its fixed to show proper count
            // incompleteJobs={incompleteJobs}
        >
            {t !== undefined && (
                <StandaloneTaskApp
                    errorMessage="Error rendering task"
                    onBack={handleBack}
                    mode={"DASH"}
                    appUuid={appID}
                    token={apiKey || "No api key provided"}
                    toolbarRef={toolbarRef}
                    blobUploader={handleUploadBlob}
                    mlActionProcessor={mlActionProcessor}
                    jobID={jobID}
                    protocol={t.protocol}
                    visibility={t.visibility}
                    filter={filter}
                    selectedJobs={selectedJobs}
                    // onUpdateIncomplete={setIncompleteJobs}
                    appFeedback={applicationFeedbackOptions}
                    labelerId={userId}
                    shouldPollPrelabels={
                        getValue(prelabelStatusFetch) === "active"
                    }
                    isOnboarding={isOnboarding}
                    onTaskSubmit={onTaskSubmit}
                    onError={handleError}
                    additionalLoadingNode={
                        <AppDetailLink
                            className="absolute bottom-0 mb-20"
                            orgUsername={orgUsername}
                            appID={isOnboarding ? appID : undefined}
                        />
                    }
                    fallback={<Loader absolute={true} mask={false} />}
                />
            )}
        </LabelingLayout>
    )
}
